import { Injectable } from "@angular/core";

import { schemaCompletionSource, SQLConfig } from "@codemirror/lang-sql";
import { Completion, CompletionContext, CompletionResult, CompletionSource } from "@codemirror/autocomplete";

import {
    SqlSchema,
    SqlLanguageSpec,
    SqlSchemaCompletionLevel,
    SqlSchemaCompletionConfig
} from "../models";
import { CodeEditorBlock } from "../../models";
import { SqlEditorSchemaVisitorService } from "./sql-editor-schema-visitor.service";
import { SqlEditorLanguageConfigService } from "./sql-editor-language-config.service";


@Injectable()
export class SqlEditorSchemaCompletionService {

    constructor(private configurationService: SqlEditorLanguageConfigService,
                private schemaVisitorService: SqlEditorSchemaVisitorService) {
    }

    public getSchemaCompletionSource(spec: SqlLanguageSpec): CompletionSource {
        const config: SQLConfig = this.configurationService.getSqlConfiguration(spec);

        const source: CompletionSource = schemaCompletionSource(config);

        return context => this.schemaAutocomplete(source, context);
    }

    private async schemaAutocomplete(source: CompletionSource, context: CompletionContext): Promise<CompletionResult> {
        try {
            if (!source || !context) return null;

            // Get the block of text preceding the cursor.
            const before: CodeEditorBlock = this.getBeforeBlock(context);

            // Prevent the @codemirror/lang-sql bug that causes schema completion to trigger if block starts in dot.
            if (!before || before.text.startsWith(".")) return null;

            return source(context);

        } catch (error) {
            return null;
        }
    }

    public getLazySchemaCompletionSource(config: SqlSchemaCompletionConfig): CompletionSource {
        return context => this.lazySchemaAutocomplete(config, context);
    }

    private async lazySchemaAutocomplete(config: SqlSchemaCompletionConfig, context: CompletionContext): Promise<CompletionResult> {
        if (!context) return null;

        switch (this.getSchemaCompletionLevel(context)) {

            case SqlSchemaCompletionLevel.Root:
                return this.rootSchemaAutocomplete(config, context);

            case SqlSchemaCompletionLevel.Children:
                return this.childrenSchemaAutocomplete(config, context);
        }
    }

    private async rootSchemaAutocomplete(config: SqlSchemaCompletionConfig, context: CompletionContext): Promise<CompletionResult> {
        try {
            if (!context || !config?.spec) return null;

            const before: CodeEditorBlock = this.getBeforeBlock(context);

            // Do not trigger root schema autocompletion if there is a dot
            // in the identifier prefix (it points to a children schema).
            if (!before || before.text.includes(".")) return null;

            // Load the schemas only when necessary, and update.
            config.spec.schemas ??= await config.getSchemas?.() ?? [];

            const schemas: SqlSchema[] = config.spec.schemas;

            if (!schemas?.length) return null;

            const completions: Completion[] = this.configurationService.convertToBasicCompletions(schemas);

            return {
                from: before.from ?? context.pos,
                options: completions,
                validFor: /\S+/
            }

        } catch (error) {
            return null;
        }
    }

    private async childrenSchemaAutocomplete(config: SqlSchemaCompletionConfig, context: CompletionContext): Promise<CompletionResult> {
        try {
            if (!context || !config?.spec?.schemas?.length) return null;

            const before: CodeEditorBlock = this.getBeforeBlock(context);

            // Do not trigger children schema autocompletion if the identifier prefix does not end in a dot
            // (it points to a root schema or a previous children autocompletion is taking care of this one).
            if (!before || !before.text.endsWith(".")) return null;

            // Get the prefix of the schema, removing the trailing dot.
            const prefix: string = before.text.slice(0, -1);

            // Convert it into a path into the schema tree.
            const path: string[] = prefix.split(".");

            const schema: SqlSchema = this.schemaVisitorService.findSchema(config.spec, path);

            if (!schema) return null;

            // Get the children of this schema from database only when necessary, and update.
            schema.children ??= await config.getChildren?.(schema) ?? [];

            if (!schema.children?.length) return null;

            const completions: Completion[] = this.configurationService.convertToBasicCompletions(schema.children);

            return {
                from: before.to ?? context.pos,
                options: completions,
                validFor: /\S+/
            }

        } catch (error) {
            return null;
        }
    }

    private getSchemaCompletionLevel(context: CompletionContext): SqlSchemaCompletionLevel {
        if (!context) return null;

        // Get the block of text preceding the character.
        const before: CodeEditorBlock = this.getBeforeBlock(context);

        if (!before) return null;

        if (!before.text.includes(".")) return SqlSchemaCompletionLevel.Root;

        if (before.text.endsWith(".")) return SqlSchemaCompletionLevel.Children;

        return null;
    }

    private getBeforeBlock(context: CompletionContext): CodeEditorBlock {
        return context.matchBefore(/\S+$/);
    }
}